import { useEffect } from "react";

import { SiteFooter } from "../components/SiteFooter";
import { SiteHeader } from "../components/SiteHeader";

export function PrivacyPage() {
  useEffect(() => {
    document.title = "Privacy policy — Backbone";
    document
      .querySelector<HTMLLinkElement>('link[rel="canonical"]')
      ?.setAttribute("href", "https://backbonesystems.ai/privacy");
  }, []);

  return (
    <div className="site-frame legal-page">
      <SiteHeader />
      <main className="page-shell legal-main">
        <div className="content-column">
          <header className="legal-hero">
            <h1>Privacy Policy</h1>
            <p>Effective August 9, 2026</p>
          </header>
          <article className="legal-copy">
            <p>
              Backbone Systems, Inc. ("Backbone," "we," "us," or "our") provides technology services
              to the healthcare sector. This Privacy Policy describes how we collect, use, and share
              personal information through our website, products, and services (collectively, the
              "Services").
            </p>
            <p>
              This policy does not cover Protected Health Information ("PHI") that we process on
              behalf of healthcare provider customers under a Business Associate Agreement ("BAA").
              Our handling of PHI is governed by HIPAA, the applicable BAA, and our customers' own
              privacy notices.
            </p>

            <section>
              <h2>Who we are</h2>
              <p>
                Backbone Systems is a C corporation located at 12864 Viscaino Road, Los Altos Hills,
                CA 94022. For privacy questions, contact us at{" "}
                <a href="mailto:security@backbonesystems.ai">security@backbonesystems.ai</a>.
              </p>
            </section>

            <section>
              <h2>Information we collect</h2>
              <p>Depending on how you interact with the Services, we may collect:</p>
              <ul>
                <li>
                  <strong>Information you provide to us,</strong> such as contact details, account
                  credentials, billing and payment information, support requests, sales inquiries,
                  and feedback.
                </li>
                <li>
                  <strong>Information collected automatically,</strong> such as IP address, browser
                  type, device identifiers, operating system, pages viewed, links clicked, and
                  similar usage information.
                </li>
                <li>
                  <strong>Information from third parties,</strong> including information from our
                  customers when they identify an authorized user, service providers, and publicly
                  available sources.
                </li>
              </ul>
              <p>
                This public website does not offer account registration and does not collect payment
                information or PHI.
              </p>
            </section>

            <section>
              <h2>How we use information</h2>
              <p>We use personal information to:</p>
              <ul>
                <li>provide, maintain, and improve the Services;</li>
                <li>process transactions and manage accounts;</li>
                <li>authenticate users and secure our systems;</li>
                <li>respond to inquiries and provide customer support;</li>
                <li>
                  send service-related communications and, where permitted, marketing
                  communications;
                </li>
                <li>comply with legal obligations and enforce our agreements; and</li>
                <li>analyze usage to develop new features and services.</li>
              </ul>
            </section>

            <section>
              <h2>How we share information</h2>
              <p>We may share personal information with:</p>
              <ul>
                <li>
                  service providers that support hosting, analytics, payment processing, email
                  delivery, or customer support, subject to confidentiality and data-protection
                  obligations;
                </li>
                <li>
                  healthcare customers and their authorized users, as necessary to deliver the
                  Services;
                </li>
                <li>professional advisors such as lawyers, accountants, and auditors; and</li>
                <li>
                  authorities or other parties when required by law or legal process, or when
                  necessary to protect rights, safety, or property.
                </li>
              </ul>
              <p>
                We do not sell personal information or share it for cross-context behavioral
                advertising.
              </p>
            </section>

            <section>
              <h2>Cookies and tracking</h2>
              <p>
                This public website does not set analytics or advertising cookies. Other Backbone
                Services may use cookies or similar technologies to operate, remember preferences,
                protect accounts, analyze traffic, and improve performance. You can control cookies
                through your browser settings, although disabling essential cookies may affect
                functionality.
              </p>
            </section>

            <section>
              <h2>Data security</h2>
              <p>
                We maintain administrative, technical, and physical safeguards designed to protect
                personal information against unauthorized access, disclosure, alteration, or
                destruction. No system is completely secure, and we cannot guarantee absolute
                security.
              </p>
            </section>

            <section>
              <h2>Data retention</h2>
              <p>
                We retain personal information for as long as needed to provide the Services, comply
                with legal obligations, resolve disputes, and enforce our agreements. Retention
                periods vary based on the type of information and the purpose for which it was
                collected.
              </p>
            </section>

            <section>
              <h2>Your choices</h2>
              <ul>
                <li>
                  <strong>Marketing emails:</strong> You can opt out using the unsubscribe link in a
                  marketing email or by contacting us.
                </li>
                <li>
                  <strong>Account information:</strong> You can update or correct account
                  information by signing in or contacting us.
                </li>
                <li>
                  <strong>Do Not Track:</strong> Our Services do not currently respond to Do Not
                  Track browser signals.
                </li>
                <li>
                  <strong>California residents:</strong> California's "Shine the Light" law (Cal.
                  Civ. Code § 1798.83) permits California residents to request information about
                  disclosures of personal information to third parties for their direct marketing
                  purposes. We do not currently make such disclosures. To make a request, email{" "}
                  <a href="mailto:security@backbonesystems.ai">security@backbonesystems.ai</a>.
                </li>
              </ul>
            </section>

            <section>
              <h2>Children's privacy</h2>
              <p>
                The Services are not directed to children under 13, and we do not knowingly collect
                personal information from children under 13. If you believe we have collected
                information from a child under 13, contact us and we will delete it.
              </p>
            </section>

            <section>
              <h2>International users</h2>
              <p>
                The Services are operated from the United States. If you access them from outside
                the United States, your information may be transferred to and processed in the
                United States, which may have different data-protection laws than your country.
              </p>
            </section>

            <section>
              <h2>Changes to this policy</h2>
              <p>
                We may update this Privacy Policy from time to time. Changes will be posted on this
                page with a new effective date. Material changes will be communicated as required by
                law.
              </p>
            </section>

            <section>
              <h2>Contact us</h2>
              <p>
                Backbone Systems
                <br />
                12864 Viscaino Road
                <br />
                Los Altos Hills, CA 94022
                <br />
                Email: <a href="mailto:security@backbonesystems.ai">security@backbonesystems.ai</a>
              </p>
            </section>
          </article>
        </div>
      </main>
      <SiteFooter />
    </div>
  );
}
